/**
 * Email verification success screen
 * Shows after successful email verification with auto-redirect
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../lib/api';
import { CheckCircle, Loader2 } from 'lucide-react';

export default function VerifyEmailSuccess() {
  const [countdown, setCountdown] = useState(3);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useToast();

  // Get verification token from URL
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationError('Invalid verification link');
        setIsVerifying(false);
        return;
      }

      try {
        await api.post('/auth/verify', { token });

        success('Email verified successfully!', {
          description: 'You can now complete your profile setup.',
        });

        setIsVerifying(false);

        // Start countdown for auto-redirect
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              navigate('/onboarding/personal-info', { replace: true });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(timer);
      } catch (err) {
        console.error('Email verification failed:', err);

        let errorMessage = 'Verification failed';
        if (err instanceof Error) {
          errorMessage = err.message;
        }

        setVerificationError(errorMessage);
        setIsVerifying(false);

        error('Verification failed', {
          description: errorMessage,
        });
      }
    };

    verifyEmail();
  }, [token, navigate, success, error]);

  // Handle manual navigation
  const handleContinue = () => {
    navigate('/onboarding/personal-info', { replace: true });
  };

  const handleResendVerification = () => {
    navigate('/auth/verify', { replace: true });
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-lilac-900 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-blueviolet animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-blueviolet mb-2">
              Verifying Email...
            </h1>
            <p className="font-body text-night-500">
              Please wait while we verify your email address.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Error state
  if (verificationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-error-light rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-error-dark" />
            </div>
            <h1 className="font-display text-3xl font-bold text-error-dark mb-2">
              Verification Failed
            </h1>
            <p className="font-body text-night-500 mb-4">{verificationError}</p>
            {verificationError.includes('expired') && (
              <p className="font-body text-night-500">
                The verification link may have expired.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <Button onClick={handleResendVerification} className="w-full">
              Get New Verification Link
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-success rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-success-dark" />
          </div>
          <h1 className="font-display text-3xl font-bold text-blueviolet mb-2">Email Verified!</h1>
          <p className="font-body text-night-500">Your email has been successfully verified.</p>
        </div>

        {/* Next Steps */}
        <div className="mb-8">
          <p className="font-body text-sm text-night-500 text-center mb-4">
            Now let's set up your caregiver profile to start sharing your availability.
          </p>

          {countdown > 0 && (
            <div className="text-center">
              <p className="font-body text-sm text-blueviolet">
                Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <Button onClick={handleContinue} className="w-full">
            Continue to Profile Setup
          </Button>
        </div>
      </Card>
    </div>
  );
}
